import React, { createContext, useContext, useState, useEffect } from 'react';
import { getLeagueSettings, updateLeagueSettings, getEffectiveSettings as getEffectiveSettingsApi } from '../adminApi';

const LeagueSettingsContext = createContext();

export const useLeagueSettings = () => {
  const context = useContext(LeagueSettingsContext);
  if (!context) {
    throw new Error('useLeagueSettings must be used within a LeagueSettingsProvider');
  }
  return context;
};

export const LeagueSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    show_locks: true,
    show_lockers: true,
    allow_duplicates: false,
    allow_future_week_locks: false,
    apply_next_week: false,
    current_mode: 'Full visibility with no duplicates'
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch league settings on mount
  useEffect(() => {
    fetchLeagueSettings();
  }, []);

  const fetchLeagueSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getLeagueSettings();
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching league settings:', err);
      setError('Failed to load league settings');
    } finally {
      setLoading(false);
    }
  };

    const updateLeagueSettingsContext = async (newSettings) => {
    try {
      setLoading(true);
      setError(null);

      const response = await updateLeagueSettings(newSettings);
      setSettings(response.data.settings);
      return { success: true, message: response.data.message };
    } catch (err) {
      console.error('Error updating league settings:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update league settings';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveSettings = async (weekNumber) => {
    try {
      const response = await getEffectiveSettingsApi(weekNumber);
      return response.data;
    } catch (err) {
      console.error('Error fetching effective settings:', err);
      // Fall back to current settings if API fails
      return settings;
    }
  };

  // Helper functions for conditional rendering
  const canSeeLockers = (weekNumber, userHasLocked, userLocksCount, maxPicks) => {
    return settings.show_lockers || (userHasLocked || userLocksCount >= maxPicks);
  };

  const canSeeLocks = () => {
    return settings.show_locks;
  };

  const allowDuplicates = () => {
    return settings.allow_duplicates;
  };

  const allowFutureWeekLocks = () => {
    return settings.allow_future_week_locks;
  };

  const getSettingsDescription = () => {
    return settings.current_mode;
  };



  const value = {
    settings,
    loading,
    error,
    fetchLeagueSettings,
    updateLeagueSettings: updateLeagueSettingsContext,
    getEffectiveSettings,
    canSeeLockers,
    canSeeLocks,
    allowDuplicates,
    allowFutureWeekLocks,
    getSettingsDescription
  };

  return (
    <LeagueSettingsContext.Provider value={value}>
      {children}
    </LeagueSettingsContext.Provider>
  );
};
